import React, { useState, useContext } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { httpsCallable } from 'firebase/functions';
import { Box, Button, Divider, Grid, Typography, Alert, useMediaQuery, Stack, AlertTitle, styled } from '@mui/material';

import PaymentContext from '../PaymentContext';
import { TreatmentTerms } from './TreatmentTerms';
import { functions } from '../../../firebase-config';
import { ConfirmationModalWrapper } from '../../ConfirmationModalWrapper';
import {
  getCatalogueId,
  getValueOrFallback,
  trimObjectStringValues,
  getProductDetailedName,
  ensureCorrectPluralForm,
  capitalizeFirstLetterOfEachWord,
} from '../../../utils';

/**
 * @type {import('firebase/functions').HttpsCallable<
 *  { scriptOnlyCheckoutProducts: ScriptOnlyCheckoutItems[]; externalPharmacy: ExternalPharmacyInfo; },
 *  CloudFunctionGenericReturnDataType,
 * >}
 */
const scriptOnlyHandler = httpsCallable(functions, 'scriptOnlyHandler');

// These error messages are also used in the backend.
// Non-critical errors (that don’t interrupt the process) have been excluded.
// Messages have been rewritten to be clearer and more patient-friendly.
const ERROR_MESSAGES = {
  USER_DATA_DOES_NOT_EXIST: 'No user data was found.',
  USER_LOCKED: 'This user account is currently locked.',
  USER_DISABLED: 'This user account is currently disabled.',
  USER_ID_NOT_FOUND: 'The specified user ID could not be found.',
  USER_EMAIL_NOT_FOUND: 'The specified user email could not be found.',
  REQUEST_IN_COOLDOWN: 'Please wait before submitting another request.',
  SUPPORT_ID_NOT_FOUND: 'Support ID is missing from environment settings.',
  CATALOGUE_ITEM_NOT_FOUND: 'One or more selected catalogue items could not be found.',
  DISPENSE_PENDING: 'One or more products are already at a pharmacy awaiting dispensing.',
  PRODUCT_PENDING_APPROVAL: 'This product is pending approval and cannot be processed yet.',
  HEALTH_CATEGORY_NOT_FOUND: 'No health category was found for the selected catalogue items.',
  DEFAULT_ERROR: 'Something went wrong while processing your request. Please try again later.',
  CATALOGUE_HEALTH_CATEGORY_MISMATCH: 'All catalogue items must belong to the same health category.',
  SCRIPT_ONLY_CONFIG_NOT_ALLOWED: 'Script-only configuration is not permitted for this health category.',
  PRODUCT_NOT_FOUND_IN_PATIENT_PRESCRIPTIONS: "The product was not found in the patient's prescriptions.",
  PRODUCT_OVER_THE_COUNTER: 'This is an over-the-counter product and cannot be requested as script-only.',
  SCRIPT_HANDLER_NOT_ALLOWED: 'You are not allowed to request a script-only handler with your current role.',
};
/**
 * @callback ConvertIfPluralFunction
 * @param {string} word - The input word to check or convert.
 * @returns {string} - The transformed word.
 */

/**
 * @param {SelectedTreatments} items
 * @return {{ id: string, quantity: number }[]}
 */
const groupAndCount = (items) => {
  const itemMappings = items.reduce((acc, item) => {
    if (!acc?.[item]) {
      return { ...acc, [item]: 1 };
    }

    return { ...acc, [item]: acc[item] + 1 };
  }, {});

  return Object.entries(itemMappings).map(([id, quantity]) => ({ id, quantity }));
};

/**
 * @param {object} params
 * @param {SelectedTreatments} params.selectedTreatments
 * @param {CatalogueMap} params.catalogue
 * @return {ScriptOnlyCheckoutItems[]}
 */
const transformCheckoutItems = ({ selectedTreatments, catalogue }) => {
  const transformedTreatments = groupAndCount(selectedTreatments);

  return transformedTreatments.map(({ id, quantity }) => {
    const product = catalogue[id] || {};

    return {
      name: getProductDetailedName({ productData: product || {} }),
      quantity: getValueOrFallback(quantity, 1),
      id: getCatalogueId(id),
    };
  });
};

const GreyedBox = styled(Box)(({ theme }) => ({
  borderColor: theme.palette.divider,
  borderWidth: '1px',
  borderStyle: 'solid',
  padding: theme.spacing(1),
  borderRadius: theme.spacing(0.5),
  backgroundColor: theme.palette.action.hover,
}));

/**
 * @param {object} props
 * @param {ExternalPharmacyInfo} props.externalPharmacy
 * @param {ScriptOnlyCheckoutItems[]} props.scriptOnlyCheckoutProducts
 * @param {string} props.healthCategoryCapitalised
 * @param {ConvertIfPluralFunction} props.convertIfPlural
 * @returns {JSX.Element}
 */
const Description = ({ externalPharmacy, scriptOnlyCheckoutProducts, healthCategoryCapitalised, convertIfPlural }) => (
  <Stack direction="column" gap={2}>
    <Stack direction="column" gap={1}>
      <Typography variant="body2">
        You are requesting to send your active <strong>{healthCategoryCapitalised}</strong>
        {convertIfPlural(' prescription')} to a pharmacy not affiliated with Candor.
      </Typography>
      <Typography variant="body2">
        Our support team will first contact the pharmacy to confirm they can accept your prescriptions before
        proceeding.
      </Typography>
    </Stack>

    <div>
      <Typography fontWeight="bold" gutterBottom>
        Pharmacy Details
      </Typography>
      <GreyedBox>
        <Typography>
          <b>Name:</b> {externalPharmacy?.name || 'N/A'}
        </Typography>
        <Typography>
          <b>Phone:</b> {externalPharmacy?.phone || 'N/A'}
        </Typography>
        <Typography>
          <b>Address:</b> {externalPharmacy?.address || 'N/A'}
        </Typography>
      </GreyedBox>
    </div>

    <div>
      <Typography fontWeight="bold" gutterBottom>
        Requested {convertIfPlural('Item')}/{convertIfPlural('Product')}
      </Typography>
      <GreyedBox as="ul" sx={{ pl: 4 }}>
        {scriptOnlyCheckoutProducts.map(({ name, id }) => (
          <li key={id}>
            <Typography>{name}</Typography>
          </li>
        ))}
      </GreyedBox>
    </div>

    <Alert icon={false} severity="info">
      <AlertTitle>Next Steps</AlertTitle>
      <ol style={{ margin: 0, paddingLeft: 24, gap: 5 }}>
        <li>We&apos;ll confirm with the pharmacy before sending your prescriptions.</li>
        <li>You&apos;ll receive a notification once the request has been approved and sent.</li>
        <li>You may need to contact the pharmacy to organise collection.</li>
      </ol>
    </Alert>
  </Stack>
);

// NOTE: add there the PharmacyInformation component in the future when deprecating the script mode
export const ScriptOnlyCheckout = () => {
  const {
    termsRead,
    scriptMode,
    pharmacyConsentRead,
    selectedTreatments,
    catalogue,
    formName,
    preferredPharmacy: preferredPharmacyWithItems,
  } = useContext(PaymentContext);
  const navigate = useNavigate();
  const [isError, setIsError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  if (!scriptMode) {
    return null;
  }

  const healthCategoryCapitalised = capitalizeFirstLetterOfEachWord(formName);
  const scriptOnlyCheckoutProducts = transformCheckoutItems({ selectedTreatments, catalogue });
  // Extracting prices as it can now be deprecated from this process.
  // The prices field was required and actively used in the previous script mode implementation. product-handler.ts:89
  const { prices, ...preferredPharmacy } = preferredPharmacyWithItems;
  /** @type {ExternalPharmacyInfo} */
  const sanitisedPreferredPharmacy = trimObjectStringValues(preferredPharmacy);

  /** @type {ConvertIfPluralFunction} */
  const convertIfPlural = (word) => ensureCorrectPluralForm({ word, count: scriptOnlyCheckoutProducts.length });

  const handleScriptOnlySubmission = async () => {
    // To be refined, this will handle the script only submission
    try {
      await toast
        .promise(
          async () => {
            const { data } = await scriptOnlyHandler({
              scriptOnlyCheckoutProducts,
              externalPharmacy: sanitisedPreferredPharmacy,
            });

            if (data?.error) {
              throw new Error(data.error);
            }
          },
          {
            loading: `Submitting your ${convertIfPlural('script')}...`,
            success: () => {
              navigate('/script-only-request-successful');
              return `${convertIfPlural('Script')} submitted successfully!`;
            },
          },
        )
        .unwrap();
    } catch (error) {
      setIsError(true);
      toast.error(ERROR_MESSAGES?.[error?.message] || ERROR_MESSAGES.DEFAULT_ERROR);
    }
  };

  return (
    <Grid
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        p: '20px',
      }}
    >
      <Typography variant="h5" gutterBottom>
        Request to Send {convertIfPlural('Script')} to External Pharmacy
      </Typography>

      <Box
        sx={{
          py: '2rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          width: '100%',
          '@media (max-width: 600px)': {
            gap: '9px',
          },
        }}
      >
        {scriptOnlyCheckoutProducts.map(({ id, name }) => (
          <Box
            key={id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography>{name}</Typography>
          </Box>
        ))}

        <Divider />
      </Box>

      <TreatmentTerms modalOpen={modalOpen} setModalOpen={setModalOpen} />

      <ConfirmationModalWrapper
        title={`Request to Send ${convertIfPlural('Prescription')} to External Pharmacy`}
        description={
          <Description
            externalPharmacy={sanitisedPreferredPharmacy}
            scriptOnlyCheckoutProducts={scriptOnlyCheckoutProducts}
            healthCategoryCapitalised={healthCategoryCapitalised}
            convertIfPlural={convertIfPlural}
          />
        }
        onConfirm={handleScriptOnlySubmission}
        titleIcon={null}
        confirmColour="primary"
        confirmText="Submit"
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <Button variant="contained" disabled={!termsRead || !pharmacyConsentRead || isError}>
          Submit Request
        </Button>
      </ConfirmationModalWrapper>
    </Grid>
  );
};
